import { gsap } from "gsap";

// Prevent GSAP DOM errors from crashing
gsap.config({ autoSleep: 60, force3D: false });
if (typeof window !== "undefined") {
  window.addEventListener("error", (event: ErrorEvent) => {
    if (event.message && event.message.includes("removeChild")) {
      console.warn("GSAP DOM node error prevented:", event.message);
      event.preventDefault?.();
    }
  });
}

let isInitialized = false;

/**
 * Initialize custom cursor with hover effects on interactive elements.
 * Respects coarse pointer devices (touch) by hiding the custom cursor.
 * Returns cleanup function to remove event listeners.
 */
export const initCursor = () => {
  if (isInitialized) return () => { };

  const dot = document.getElementById("cursor-dot");
  const ring = document.getElementById("cursor-ring");
  const project = document.getElementById("cursor-project");

  if (!dot || !ring) return () => { };

  // Hide custom cursor on touch devices
  const isCoarsePointer =
    (window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches) ||
    (typeof navigator !== "undefined" && navigator.maxTouchPoints > 0);

  if (isCoarsePointer) {
    [dot, ring, project].forEach((el) => {
      if (el) el.style.display = "none";
    });
    return () => { };
  }

  isInitialized = true;

  // Position cursors at center
  const tracked = [dot, ring];
  if (project) tracked.push(project);
  gsap.set(tracked, { xPercent: -50, yPercent: -50 });

  // Create GSAP animation tweens
  const moveDotX = gsap.quickTo(dot, "x", {
    duration: 0.12,
    ease: "power3.out",
  });
  const moveDotY = gsap.quickTo(dot, "y", {
    duration: 0.12,
    ease: "power3.out",
  });
  const moveRingX = gsap.quickTo(ring, "x", {
    duration: 0.35,
    ease: "power3.out",
  });
  const moveRingY = gsap.quickTo(ring, "y", {
    duration: 0.35,
    ease: "power3.out",
  });
  const moveProjectX = project
    ? gsap.quickTo(project, "x", { duration: 0.2, ease: "power3.out" })
    : null;
  const moveProjectY = project
    ? gsap.quickTo(project, "y", { duration: 0.2, ease: "power3.out" })
    : null;

  let rafId: number | null = null;
  let lastPointer: { x: number; y: number } | null = null;

  // Throttle cursor movement with RAF
  const handleMove = (event: PointerEvent) => {
    lastPointer = { x: event.clientX, y: event.clientY };
    if (rafId) return;

    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      if (!lastPointer) return;

      const { x, y } = lastPointer;
      moveDotX(x);
      moveDotY(y);
      moveRingX(x);
      moveRingY(y);

      if (moveProjectX && moveProjectY) {
        moveProjectX(x);
        moveProjectY(y);
      }
    });
  };

  // Scale down on pointer down
  const handleDown = () => {
    try {
      if (document.contains(ring) && document.contains(dot)) {
        gsap.to([ring, dot], { scale: 0.85, duration: 0.15, ease: "power2.out" });
      }
    } catch (e) {
      console.warn("Error in cursor scale down:", e);
    }
  };

  // Scale back on pointer up
  const handleUp = () => {
    try {
      if (document.contains(ring) && document.contains(dot)) {
        gsap.to([ring, dot], { scale: 1, duration: 0.2, ease: "power2.out" });
      }
    } catch (e) {
      console.warn("Error in cursor scale up:", e);
    }
  };

  // Move cursor
  window.addEventListener("pointermove", handleMove, { passive: true });
  window.addEventListener("pointerdown", handleDown);
  window.addEventListener("pointerup", handleUp);

  // Combined hover effects for both projects and interactive elements
  const PROJECT_SELECTOR =
    ".latest-work__device, .works-page__card, .work-detail__more-card";
  const INTERACTIVE_SELECTOR = "a, button, .btn-neon, .nav-link";
  const RING_INTERACTIVE_STYLE = {
    scale: 1.45,
    borderColor: "rgba(96, 221, 255, 0.9)",
    boxShadow: "0 0 18px rgba(96, 221, 255, 0.6)",
    duration: 0.2,
    ease: "power2.out",
  };
  const RING_DEFAULT_STYLE = {
    scale: 1,
    borderColor: "rgba(122, 87, 219, 0.55)",
    boxShadow: "0 0 14px rgba(122, 87, 219, 0.4)",
    duration: 0.2,
    ease: "power2.out",
  };

  let projectHoverActive = false;
  let activeInteractive: Element | null = null;

  const showProjectCursor = () => {
    if (!project || !document.contains(project)) return;
    if (!document.contains(dot) || !document.contains(ring)) return;
    gsap.to(project, {
      autoAlpha: 1,
      scale: 1,
      duration: 0.2,
      ease: "power2.out",
    });
    gsap.to([dot, ring], {
      autoAlpha: 0,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  const hideProjectCursor = () => {
    if (!project || !document.contains(project)) return;
    if (!document.contains(dot) || !document.contains(ring)) return;
    gsap.to(project, {
      autoAlpha: 0,
      scale: 0.9,
      duration: 0.2,
      ease: "power2.inOut",
    });
    gsap.to([dot, ring], {
      autoAlpha: 1,
      duration: 0.2,
      ease: "power2.out",
    });
  };

  const handleCombinedOver = (event: PointerEvent) => {
    try {
      const target = event.target as Element;
      // Check for project card first
      if (project && document.contains(project)) {
        const projectCard = target?.closest?.(PROJECT_SELECTOR);
        if (projectCard && !projectHoverActive) {
          projectHoverActive = true;
          showProjectCursor();
          return;
        }
      }

      // Check for interactive element
      const interactive = target?.closest?.(INTERACTIVE_SELECTOR);
      if (!interactive || interactive === activeInteractive) return;
      if (!document.contains(ring)) return;

      activeInteractive = interactive;
      gsap.to(ring, RING_INTERACTIVE_STYLE);
    } catch (e) {
      console.warn("Error in pointer over handler:", e);
    }
  };

  const handleCombinedOut = (event: PointerEvent) => {
    // Check if leaving project card
    if (projectHoverActive) {
      const relatedTarget = event.relatedTarget as Element;
      const relatedCard = relatedTarget?.closest?.(PROJECT_SELECTOR);
      if (relatedCard) return;

      projectHoverActive = false;
      hideProjectCursor();
      return;
    }

    // Check if leaving interactive element
    if (!activeInteractive) return;
    const relatedTarget = event.relatedTarget as Node;
    if (relatedTarget && activeInteractive.contains(relatedTarget)) {
      return;
    }

    if (!document.contains(ring)) return;
    activeInteractive = null;
    gsap.to(ring, RING_DEFAULT_STYLE);
  };

  window.addEventListener("pointerover", handleCombinedOver);
  window.addEventListener("pointerout", handleCombinedOut);

  // Return cleanup function
  return () => {
    window.removeEventListener("pointermove", handleMove);
    window.removeEventListener("pointerdown", handleDown);
    window.removeEventListener("pointerup", handleUp);
    window.removeEventListener("pointerover", handleCombinedOver);
    window.removeEventListener("pointerout", handleCombinedOut);

    if (rafId) {
      window.cancelAnimationFrame(rafId);
    }

    isInitialized = false;
  };
};
